import { useId, useRef, useState, type CSSProperties } from 'react';
import { MAX_IMAGE_UPLOAD_BYTES, useUploadImage } from '../../api/images.api';

/**
 * Pick an image from the computer instead of pasting a link (JT1-1922). Shows a preview, and can
 * replace or remove it. Pasting a link still works. `value` is the image's address.
 */
export function ImageUploadField({
  value,
  onChange,
  disabled,
  inputStyle,
  placeholder = 'or paste a link: https://…',
  testId = 'image-upload',
}: {
  value: string | null | undefined;
  onChange: (url: string | null) => void;
  disabled?: boolean;
  inputStyle?: CSSProperties;
  placeholder?: string;
  testId?: string;
}) {
  const fileId = useId();
  const fileRef = useRef<HTMLInputElement>(null);
  const upload = useUploadImage();
  const [error, setError] = useState<string | null>(null);

  const pick = async (file: File | undefined) => {
    if (!file) return;
    setError(null);
    if (file.size > MAX_IMAGE_UPLOAD_BYTES) {
      setError('That image is bigger than 2 MB. Choose a smaller one.');
      return;
    }
    try {
      onChange(await upload.mutateAsync(file));
    } catch (err) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setError(msg ?? 'The image could not be uploaded. Try again.');
    } finally {
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }} data-testid={testId}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        {value && (
          <img
            src={value}
            alt="Preview"
            data-testid={`${testId}-preview`}
            style={{ maxHeight: 40, maxWidth: 160, objectFit: 'contain', border: '1px solid var(--color-border-default, #ececf2)', borderRadius: 4, background: '#fff' }}
          />
        )}
        <input
          id={fileId}
          ref={fileRef}
          type="file"
          accept="image/png,image/jpeg,image/gif,image/webp"
          disabled={disabled || upload.isPending}
          onChange={(e) => pick(e.target.files?.[0])}
          style={{ display: 'none' }}
          data-testid={`${testId}-file`}
        />
        <label
          htmlFor={fileId}
          style={{ ...buttonStyle(), cursor: disabled || upload.isPending ? 'not-allowed' : 'pointer', opacity: disabled ? 0.6 : 1 }}
        >
          {upload.isPending ? 'Uploading…' : value ? 'Replace image' : 'Upload image'}
        </label>
        {value && !disabled && (
          <button type="button" onClick={() => onChange(null)} style={buttonStyle()}>
            Remove
          </button>
        )}
      </div>
      <input
        type="url"
        value={value ?? ''}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value || null)}
        placeholder={placeholder}
        style={inputStyle}
        aria-label="Image link"
      />
      <span style={{ fontSize: 11, color: 'var(--color-text-muted, #6b6b7d)' }}>PNG, JPG, GIF or WebP, up to 2 MB.</span>
      {error && (
        <span role="alert" style={{ fontSize: 12, color: 'var(--color-danger, #e03131)' }}>
          {error}
        </span>
      )}
    </div>
  );
}

function buttonStyle(): CSSProperties {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '4px 10px',
    background: '#fff',
    color: 'var(--color-text-secondary, #2b2b3c)',
    border: '1px solid var(--color-border-default, #ececf2)',
    borderRadius: 4,
    fontSize: 12,
    fontWeight: 600,
    fontFamily: 'inherit',
  };
}
